/**
 * Classify each proposed slot against the active overlay owners' busy
 * events + availability marks, producing three buckets per slot:
 *
 *   conflict  : owners with a busy event overlapping the slot range
 *   preferred : owners with a PREFERRED mark overlapping the slot range
 *   feasible  : owners with a FEASIBLE mark overlapping the slot range
 *
 * Each row carries the per-owner conflict state (`hasConflict` +
 * `conflictTitles`) so the star/checkbox popovers can show a nested
 * warning when a user has both a positive availability mark AND a
 * busy event blocking the slot.
 *
 * Pure / fully testable — no React, no Firestore.
 */

import { intervalsOverlap } from "../../../lib/intervals";

// This classifier contractually receives Date instances; keep the strict
// instanceof gate (a stray non-Date means "no signal", never a coerced match)
// and delegate the half-open overlap math to the canonical lib/intervals.
function rangesOverlap(aStart, aEnd, bStart, bEnd) {
  if (
    !(aStart instanceof Date) ||
    !(aEnd instanceof Date) ||
    !(bStart instanceof Date) ||
    !(bEnd instanceof Date)
  ) {
    return false;
  }
  return intervalsOverlap(aStart, aEnd, bStart, bEnd);
}

/**
 * Inputs:
 *   slots                : Array<{ id, start, end }>  (Date instances)
 *   owners               : Array<{ ownerId, displayName, avatarUrl, email?, isSelf? }>
 *   busyEventsByOwnerId  : { ownerId: Array<{ start, end, title? }> }
 *   marksByOwnerId       : { ownerId: Array<{ start, end, level }> }
 *                          where level ∈ {"PREFERRED", "FEASIBLE", ...}
 *   colorByOwnerId       : { ownerId: hex|null }
 *
 * Returns:
 *   { [slotId]: { conflict: Row[], preferred: Row[], feasible: Row[] } }
 *
 *   Row = {
 *     ownerId, displayName, avatarUrl, email?, isSelf,
 *     color, hasConflict, conflictTitles
 *   }
 */
export function classifySlotsAgainstOverlays({
  slots = [],
  owners = [],
  busyEventsByOwnerId = {},
  marksByOwnerId = {},
  colorByOwnerId = {},
} = {}) {
  const out = {};
  for (const slot of slots) {
    if (!slot || !(slot.start instanceof Date) || !(slot.end instanceof Date)) {
      continue;
    }
    const conflict = [];
    const preferred = [];
    const feasible = [];

    for (const owner of owners) {
      if (!owner || !owner.ownerId) continue;
      const busyEvents = busyEventsByOwnerId[owner.ownerId] || [];
      const conflictingTitles = busyEvents
        .filter((e) => rangesOverlap(e.start, e.end, slot.start, slot.end))
        .map((e) => e.title || "Busy");
      const hasConflict = conflictingTitles.length > 0;

      const ownerMarks = marksByOwnerId[owner.ownerId] || [];
      const hasPreferred = ownerMarks.some(
        (m) =>
          m.level === "PREFERRED" &&
          rangesOverlap(m.start, m.end, slot.start, slot.end),
      );
      const hasFeasible = ownerMarks.some(
        (m) =>
          m.level === "FEASIBLE" &&
          rangesOverlap(m.start, m.end, slot.start, slot.end),
      );

      const row = {
        ownerId: owner.ownerId,
        displayName: owner.displayName || null,
        avatarUrl: owner.avatarUrl || null,
        email: owner.email || null,
        isSelf: owner.isSelf === true,
        color: colorByOwnerId[owner.ownerId] || null,
        hasConflict,
        conflictTitles: conflictingTitles,
      };

      if (hasConflict) conflict.push(row);
      if (hasPreferred) preferred.push(row);
      if (hasFeasible) feasible.push(row);
    }

    out[slot.id] = { conflict, preferred, feasible };
  }
  return out;
}
